/**
 * Event Edit RBAC Guard
 * Custom guard for event edit routes that checks both admin permissions and event creator permissions
 */

import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { getRBACUtils } from '@/composables/useRBAC';
import { useProjectStore } from '@/store/project.store';
import { useUserStore } from '@/store/user.store';

/**
 * Custom RBAC guard for event edit routes
 * Allows admins, owners, and event creators to access edit routes
 */
export async function eventEditGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  try {
    const rbac = getRBACUtils();
    
    // Skip if user is not authenticated
    if (!rbac.getCurrentRole()) {
      return next();
    }

    // Extract event ID from route params
    const eventId = Array.isArray(to.params.id) ? to.params.id[0] : to.params.id;
    
    if (!eventId) {
      console.warn('[Event Edit Guard] No event ID found in route params');
      return next('/forbidden');
    }

    // Check if user has admin privileges
    const hasAdminAccess = rbac.hasAnyRole(['admin', 'owner']);
    
    // Check if user is the event creator
    const userStore = useUserStore();
    const projectStore = useProjectStore();
    const currentUserId = userStore.authUser?.uid;
    const event = projectStore.getCollectionDocumentById('project_events', eventId);
    const isEventCreator = event?.createdBy === currentUserId;
    
    if (hasAdminAccess || isEventCreator) {
      console.log(`[Event Edit Guard] Access granted to event ${eventId} for role '${rbac.getCurrentRole()}'`);
      return next();
    }
    
    console.warn(`[Event Edit Guard] Access denied to event ${eventId} for role '${rbac.getCurrentRole()}'`);
    return next('/forbidden');
    
  } catch (error) {
    console.error('[Event Edit Guard] Error checking permissions:', error);
    return next('/forbidden');
  }
}